import type React from 'react'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import MenuIcon from '@mui/icons-material/Menu'
import LightModeIcon from '@mui/icons-material/LightMode'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import { useLocation } from 'react-router-dom'
import Upload from '../../features/upload/Upload'
import UserMenu from '../../features/user/UserMenu'
import { useColorMode } from '../../design/ColorModeProvider'
import { useFileParsing } from '../providers/FileParsingContext'
import { useLoadDrawer } from '../providers/LoadDrawerContext'
import { v2Breadcrumb } from './navItems'

interface TopBarProps {
  /** Show the hamburger (mobile only). */
  showMenuButton: boolean
  onMenuClick: () => void
}

export const TopBar: React.FC<TopBarProps> = ({
  showMenuButton,
  onMenuClick,
}) => {
  const { pathname } = useLocation()
  const { mode, toggleMode } = useColorMode()
  const { setProgressPercent, setIsLoading, setApiError } = useFileParsing()
  const { toggleDrawer } = useLoadDrawer()

  return (
    <AppBar
      position="sticky"
      color="default"
      elevation={0}
      sx={{ borderBottom: 1, borderColor: 'divider' }}
    >
      <Toolbar>
        {showMenuButton && (
          <IconButton
            edge="start"
            aria-label="toggle navigation"
            onClick={onMenuClick}
            sx={{ mr: 1 }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Typography variant="h6" component="h1" noWrap sx={{ flexGrow: 1 }}>
          {v2Breadcrumb(pathname)}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Upload
            setProgressPercent={setProgressPercent}
            setIsLoading={setIsLoading}
            setError={setApiError}
          />
          <Button
            color="inherit"
            startIcon={<CloudDownloadIcon />}
            onClick={toggleDrawer}
            sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
          >
            Load
          </Button>
          <Tooltip
            title={mode === 'dark' ? 'Switch to light' : 'Switch to dark'}
          >
            <IconButton aria-label="toggle color mode" onClick={toggleMode}>
              {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>
          <UserMenu />
        </Box>
      </Toolbar>
    </AppBar>
  )
}
